import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Loader2, PenLine, Printer, Radio, Unlink, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEOHead from '@/components/SEOHead';
import { SiteFooter } from '@/components/landing/SiteFooter';
import { BracketBoard } from '@/components/bracket/BracketBoard';
import { UpgradePrompt } from '@/components/bracket/custom/UpgradePrompt';
import { useSharedBracket } from '@/components/bracket/share/useSharedBracket';
import { buildBracketModel, describeEvent } from '@/lib/bracket/model';
import { applyCustomResults, formatEventDate } from '@/lib/bracket/custom';
import { statsLinksFor } from '@/lib/bracket/stats';
import { FORMATS, baseTitle } from '@/lib/bracket/templates';
import { logoUrlOf } from '@/lib/bracket/shareApi';
import { trackBracket } from '@/lib/bracket/customTracking';
import { fmtDateTime } from '@/lib/billingApi';

const Header = ({ share, title, onPrint }) => (
  <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
    <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
      <Link to="/" className="text-lg font-black text-[#00247D] shrink-0" style={{ fontFamily: 'Barlow Condensed' }} data-testid="shared-home-link">SCHEDULE GURU</Link>
      <div className="flex items-center gap-2 min-w-0">
        {share?.is_owner && (
          <Link to={`/bracket-library?template=${share.template_id}&share=${share.id}`}>
            <Button variant="outline" size="sm" data-testid="shared-edit-btn"><PenLine className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Edit bracket</span></Button>
          </Link>
        )}
        {title && <Button variant="outline" size="sm" onClick={onPrint} data-testid="shared-print-btn"><Printer className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Print</span></Button>}
        <Link to="/bracket-library"><Button size="sm" className="bg-[#CF142B] hover:bg-[#A81022]" data-testid="shared-make-own-btn">Make your own</Button></Link>
      </div>
    </div>
  </header>
);

const Gone = () => (
  <div className="max-w-md mx-auto text-center py-24 px-4" data-testid="shared-bracket-gone">
    <Unlink className="w-12 h-12 text-slate-300 mx-auto" />
    <h1 className="text-4xl font-black text-[#00247D] mt-4" style={{ fontFamily: 'Barlow Condensed' }}>BRACKET NOT FOUND</h1>
    <p className="text-slate-600 mt-2">The organizer stopped sharing this bracket, or the link is mistyped.</p>
    <Link to="/bracket-library"><Button className="mt-6 bg-[#00247D] hover:bg-[#001A5C]" data-testid="shared-gone-library-btn">Browse free bracket templates</Button></Link>
  </div>
);

export default function SharedBracketPage() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const boardRef = useRef(null);
  const { share: s, preview, error } = useSharedBracket(shareId);
  const [prompt, setPrompt] = useState(null);
  const [blankBusy, setBlankBusy] = useState(false);

  const fmt = s && FORMATS[s.format];
  const ev = s?.event;
  const base = s ? baseTitle(s.num_teams, s.format) : '';
  const baseSub = preview ? `${preview.total_matches} games  ·  Printable bracket template` : '';
  const model = useMemo(() => {
    if (ev) return buildBracketModel({ format: ev.format, schedule: ev.schedule });
    return s && preview ? buildBracketModel({ format: s.format, schedule: applyCustomResults(preview.schedule, s.names, s.scores) }) : null;
  }, [s, ev, preview]);
  const statsLinks = useMemo(() => (ev ? statsLinksFor(ev, ev.share_token) : null), [ev]);
  const baseSettings = useMemo(() => s?.settings, [JSON.stringify(s?.settings)]); // eslint-disable-line react-hooks/exhaustive-deps
  const custom = useMemo(() => {
    if (!s) return null;
    const block = () => { setPrompt(s.is_owner ? 'export' : 'viewonly'); if (s.is_owner) trackBracket('upgrade_prompt', s.template_id); };
    return {
      writeIn: !ev, champNames: ev ? {} : s.names, watermark: s.owner_pro ? null : 'PREVIEW · MADE WITH SCHEDULE GURU FREE',
      printFallback: s.owner_pro ? null : { title: base, subtitle: baseSub, eventLogo: null },
      onBlockedPrint: block,
      gate: async () => { if (s.owner_pro) return true; block(); return false; },
    };
  }, [s, ev, base, baseSub]);

  const primary = () => {
    if (s.is_owner) { trackBracket('upgrade_click', s.template_id); navigate('/pricing?from=bracket-share'); } else navigate(`/bracket-library?template=${s.template_id}`);
  };
  const downloadBlank = async () => { setBlankBusy(true); await boardRef.current?.downloadBlank(); setBlankBusy(false); setPrompt(null); };
  const title = ev ? ev.name || base : s ? s.title || base : '';
  const subtitle = ev ? describeEvent(ev) : s ? [formatEventDate(s.date), `${s.num_teams} teams`, fmt.label].filter(Boolean).join('  ·  ') : '';

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead title={title ? `${title} Bracket` : 'Shared Bracket'} description="Tournament bracket shared with Schedule Guru." path={`/b/${shareId}`} noindex />
      <Header share={s} title={model ? title : ''} onPrint={() => boardRef.current?.run('print')} />
      {error ? <Gone /> : !model ? (
        <div className="flex justify-center py-24" data-testid="shared-bracket-loading"><Loader2 className="w-8 h-8 text-[#00247D] animate-spin" /></div>
      ) : (
        <main className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-6 animate-in fade-in duration-300" data-testid="shared-bracket-page">
          <div className="mb-4">
            <h1 className="text-4xl sm:text-5xl font-black text-[#00247D] leading-none uppercase" style={{ fontFamily: 'Barlow Condensed' }} data-testid="shared-bracket-title">{title}</h1>
            <p className="text-sm text-slate-500 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="shared-bracket-meta">
              <span>{subtitle}</span>
              <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold"><Radio className="w-3.5 h-3.5" />Live · {ev ? 'official results' : `updated ${fmtDateTime(s.updated_at)}`}</span>
              {ev?.share_token && (
                <Link to={`/share/${ev.share_token}`} className="inline-flex items-center gap-1 font-semibold text-[#00247D] hover:underline" data-testid="shared-tournament-page-link">
                  <BarChart3 className="w-3.5 h-3.5" />Full tournament page, stats &amp; box scores
                </Link>
              )}
            </p>
          </div>
          <BracketBoard ref={boardRef} model={model} title={title} subtitle={subtitle} logos={ev?.team_logos || {}}
            eventLogo={ev?.event_logo ? logoUrlOf(ev.event_logo) : logoUrlOf(s.logo_path) || null} statsLinks={statsLinks}
            showCourt={(ev?.num_courts || 1) > 1}
            storageKey={`bracket-layout:share:${s.id}`} baseSettings={baseSettings} fileName={`${s.num_teams}-team-bracket`} custom={custom} />
        </main>
      )}
      <UpgradePrompt kind={prompt} onClose={() => setPrompt(null)} onPrimary={primary} onBlank={downloadBlank} blankBusy={blankBusy} />
      <SiteFooter />
    </div>
  );
}
